import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Job } from '../../types/job';
import JobTags from './JobTags';
import JobPublishedDate from './JobPublishedDate';
import JobListingType from './JobListingType';
import {
  getJobExpirationInfo,
  getJobSelectionInfo,
  getJobLifecycleState,
  canRenewJob,
  isJobRenewable,
  getJobRecruiterDisplay,
} from '../../utils/jobUtils';
import { useJobStore } from '../../stores/jobStore';

const AVATAR_COLOR_CLASSES = ['bg-primary', 'bg-mauve', 'bg-secondary'];

const getJobInitials = (title: string): string => {
  const words = title.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return 'OF';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const getAvatarColorClass = (index: number): string =>
  AVATAR_COLOR_CLASSES[index % AVATAR_COLOR_CLASSES.length];

export interface JobListCardProps {
  job: Job;
  index: number;
  onManageClick: (job: Job) => void;
}

const JobListCard: React.FC<JobListCardProps> = ({ job, index, onManageClick }) => {
  const { t, i18n } = useTranslation('jobs');
  const { renewJob } = useJobStore();
  const canManage = job.status !== 'cancelled';
  const expirationInfo = getJobExpirationInfo(job, t, i18n.language);
  const selectionInfo = getJobSelectionInfo(job, t, i18n.language);
  const lifecycleState = getJobLifecycleState(job);
  const [renewing, setRenewing] = useState(false);
  const recruiterDisplay = getJobRecruiterDisplay(job);

  const handleRenew = async () => {
    setRenewing(true);
    try {
      await renewJob(job.id);
      toast.success(t('renew.success'));
    } catch {
      toast.error(t('renew.error'));
    } finally {
      setRenewing(false);
    }
  };

  return (
    <article
      className="rounded-[18px] border-2 border-[#BFD3DE] bg-white p-4 shadow-[0px_3px_12px_rgba(13,45,57,0.14)] md:px-6 md:py-5"
      data-testid="job-list-card"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-3 md:items-center md:gap-4">
          <div
            className={`${getAvatarColorClass(index)} flex size-[52px] shrink-0 items-center justify-center rounded-full text-[22px] font-bold leading-none text-white shadow-[0px_3px_12px_rgba(13,45,57,0.14)]`}
          >
            {getJobInitials(job.title)}
          </div>

          <div className="min-w-0">
            <h2 className="truncate text-[27px] font-semibold leading-[35px] text-[#304659]">
              {job.title}
            </h2>
            <p className="mt-1 text-sm text-secondary">
              {recruiterDisplay.context
                ? `${recruiterDisplay.name} (${recruiterDisplay.context})`
                : recruiterDisplay.name}
            </p>

            <div className="mt-1 flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
              <JobPublishedDate job={job} t={t} language={i18n.language} className="text-xs" />

              {lifecycleState === 'removed' ? (
                <span className="inline-flex items-center self-start rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {t('removed')}
                </span>
              ) : job.status === 'reviewing_quotes' ? (
                <span className="inline-flex items-center self-start rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                  {t('reviewing_quotes')}
                </span>
              ) : (
                expirationInfo &&
                (job.status === 'expired' ? (
                  <span className="inline-flex items-center self-start rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-700">
                    {t('expired')}
                  </span>
                ) : (
                  <div className="flex flex-col text-xs text-secondary">
                    <span>{expirationInfo.label}</span>
                    <span className="font-bold">{expirationInfo.value}</span>
                  </div>
                ))
              )}

              {lifecycleState !== 'removed' &&
                (job.status === 'reviewing_quotes' || !expirationInfo?.expired) &&
                selectionInfo && (
                  <div className="flex flex-col text-xs text-secondary">
                    <span>{selectionInfo.label}</span>
                    <span className="font-bold">{selectionInfo.value}</span>
                  </div>
                )}

              <JobTags job={job} t={t} variant="hub" />
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col items-stretch gap-3 md:items-end">
          <JobListingType job={job} t={t} className="text-xs" />

          {canRenewJob(job) && !isJobRenewable(job) && (
            <p className="text-xs text-secondary md:max-w-[220px] md:text-right">
              {t('renew.start_date_unavailable_notice')}
            </p>
          )}

          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            {canRenewJob(job) && isJobRenewable(job) && (
              <button
                type="button"
                className="h-10 shrink-0 whitespace-nowrap rounded-full border-2 border-primary bg-white px-6 text-[17px] font-semibold leading-[25px] text-primary transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 md:min-w-[128px]"
                onClick={handleRenew}
                disabled={renewing}
              >
                {renewing ? t('renewing') : t('renew.free')}
              </button>
            )}

            <button
              type="button"
              className="h-10 shrink-0 whitespace-nowrap rounded-full bg-primary px-6 text-[17px] font-semibold leading-[25px] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 md:min-w-[128px]"
              onClick={() => onManageClick(job)}
              disabled={!canManage}
            >
              {t('manage')}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default JobListCard;
